import React, { useEffect, useState } from 'react';
import theme from 'theme';
import { Text } from 'library';
import { styled, useMediaQuery, Grid } from '@mui/material';
import { useClient } from 'shared';
import { useUserContext } from 'UserContext';

import { PostAJob } from './components/PostAJob';
import { ReferralBanner } from './components/ReferralBanner';
import { NotificationsAndPayments } from './components/NotificationsAndPayment';
import { Reviews } from './components/Reviews';
import { Timesheets } from './components/Timesheets';
import { WelcomeModal } from './components/WelcomeModal';
import { ELCWelcomeModal } from './components/ELCWelcomeModal';

import type { DashboardSummary } from './types';
import { ActionNeededWorkers } from './components/ActionNeededWorkers';
import { PaymentStatus } from './components/PaymentStatus';
import { ScheduleACall } from './components/ScheduleACall';
import HowItWorks from './components/HowItWorks';
import AvailableTeachers from './components/AvailableTeachers';
import { TeacherShowcase } from './components/TeacherShowcase';
import PayMethodMandates from './components/PayMethodMandates';
import { ELCResourceCard } from './components/ELCResourceCard';
import { ELCEligibilityModal } from './components/ELCEligibilityModal';

export default function Dashboard() {
    const { user } = useUserContext();
    const [showRefer, setShowRefer] = useState<boolean>(true);
    const [showWelcomeModal, setShowWelcomeModal] = useState<boolean>(!!user?.welcome_attribute);
    const { data, loading, error, refresh } = useClient<DashboardSummary>({
        url: 'api/dashboard/summary/',
    });

    const small = useMediaQuery(theme.breakpoints.down('lg'));

    const elcPartner = data?.sub_pool;
    const potentialSubPool = data?.potential_sub_pool;
    const elcWelcomeAttribute = user?.sub_pool_welcome_attribute;
    const [showELCModal, setShowELCModal] = useState<boolean>(!!elcWelcomeAttribute);

    const reviewsAndTimesheets =
        !!data?.reviews?.length ||
        !!data?.timesheets?.length ||
        !!data?.missing_payment_reports?.length ||
        !!data?.sent_home_workers?.length;
    const action_required_workers = data?.action_required_workers;

    useEffect(() => setShowWelcomeModal(!!user?.welcome_attribute), [user?.welcome_attribute]);
    useEffect(() => setShowELCModal(!!elcWelcomeAttribute), [elcWelcomeAttribute?.id]);

    const isManualPayer = user?.businesses_active?.some((x) => Boolean(x.billing_cadence));

    return (
        <Page>
            {data?.show_refer && showRefer && <ReferralBanner small={small} setShowRefer={setShowRefer} />}
            <Container small={small.toString()}>
                <Text
                    variant="display"
                    id="dashboard-welcome-title"
                    textStyle={{ display: 'flex', alignItems: 'center' }}
                >
                    {user?.first_name ? `Hi ${user.first_name}!` : 'Hi!'}
                    {!data?.has_posted_jobs && !small ? <ScheduleACall small={small} /> : null}
                </Text>
                {!data?.has_posted_jobs && small ? (
                    <Row small={small.toString()}>
                        <ScheduleACall small={small} />
                    </Row>
                ) : null}
                {elcPartner && <ELCResourceCard partner={elcPartner} />}
                {!isManualPayer &&
                data?.payment.business_payment_status &&
                ['BLOCKED', 'WARNING'].includes(data?.payment.business_payment_status) ? (
                    <Row small={small.toString()}>
                        <PaymentStatus status={data?.payment.business_payment_status} />
                    </Row>
                ) : null}
                {data?.payment?.pay_mandates_needed?.length ? (
                    <PayMethodMandates neededMandates={data.payment.pay_mandates_needed} refresh={refresh} />
                ) : null}
                <Row small={small.toString()}>
                    <PostAJob data={data} loading={loading} error={!!error} refresh={refresh} small={small} />
                    <NotificationsAndPayments
                        data={data}
                        loading={loading}
                        error={!!error}
                        refresh={refresh}
                        small={small}
                    />
                </Row>
                {action_required_workers?.length ? (
                    <Row small={small.toString()}>
                        <Grid item container xs={6}>
                            <ActionNeededWorkers
                                workers={action_required_workers}
                                loading={loading}
                                small={small}
                                refresh={refresh}
                            />
                        </Grid>
                    </Row>
                ) : null}
                {(reviewsAndTimesheets || !user?.is_new_market) && (
                    <Row small={small.toString()}>
                        {reviewsAndTimesheets && (
                            <>
                                <Timesheets dashboardSummary={data} loading={loading} small={small} refresh={refresh} />
                                <Reviews loading={loading} small={small} workers={data?.reviews} />
                            </>
                        )}
                        {!user?.is_new_market && <TeacherShowcase small={small} />}
                    </Row>
                )}
                {user?.is_new_market && <AvailableTeachers small={small} />}
                {data && !data.has_paid_worker ? <HowItWorks /> : null}
                {data &&
                    !potentialSubPool &&
                    (elcPartner ? (
                        <ELCWelcomeModal
                            partner={elcPartner}
                            open={showELCModal}
                            onClose={() => setShowELCModal(false)}
                        />
                    ) : (
                        <WelcomeModal open={showWelcomeModal} onClose={() => setShowWelcomeModal(false)} />
                    ))}
                {potentialSubPool && (
                    <ELCEligibilityModal
                        partner={potentialSubPool}
                        open={showELCModal}
                        onClose={() => setShowELCModal(false)}
                    />
                )}
            </Container>
        </Page>
    );
}

const Page = styled('div')({
    width: '100%',
    height: '100%',
});

const Container = styled('div')(({ small }: { small: string }) => ({
    padding: 20,
    paddingLeft: small === 'true' ? 20 : '2%',
    paddingBottom: '5%',
}));

const Row = styled('div')(({ small }: { small: string }) => ({
    display: 'flex',
    flexDirection: small === 'true' ? 'column' : 'row',
    flexWrap: 'wrap',
    gap: 20,
    paddingTop: 20,
    width: '100%',
}));
